/**
 * File name compatibility across operating systems.
 *
 * A valid name on Linux/macOS can be illegal on Windows (and vice versa).
 * The receiver ALWAYS sanitizes the name proposed by the sender before
 * materializing the file: it never trusts the wire to build a path
 * (defense against path traversal) nor assumes the sender's OS.
 *
 * Rules applied (safe intersection Windows ∩ macOS ∩ Linux):
 * - Any path component is discarded: basename only (`/` and `\`).
 * - Windows reserved characters `< > : " | ? *` and control ones → `_`.
 * - Windows reserved names (CON, PRN, AUX, NUL, COM1-9, LPT1-9) → prefix.
 * - No trailing dot/space (Windows silently trims them).
 * - `.`/`..`/empty → default name.
 * - Unicode NFC normalization (macOS uses NFD; NFC is the interoperable canonical).
 * - Length bounded to 200 bytes preserving the extension.
 */

const WINDOWS_RESERVED = new Set([
  "CON",
  "PRN",
  "AUX",
  "NUL",
  "COM1",
  "COM2",
  "COM3",
  "COM4",
  "COM5",
  "COM6",
  "COM7",
  "COM8",
  "COM9",
  "LPT1",
  "LPT2",
  "LPT3",
  "LPT4",
  "LPT5",
  "LPT6",
  "LPT7",
  "LPT8",
  "LPT9",
]);

const DEFAULT_NAME = "archivo";
const MAX_NAME_BYTES = 200;

// Windows reserved + path separators + control 0x00-0x1F. Spaces and
// dashes are legal and common in file names: they are NOT touched. It is
// built with `new RegExp` to keep the source in printable ASCII (without
// literal control bytes in the file).
const ILLEGAL = new RegExp(String.raw`[<>:"/\\|?*\u0000-\u001f]`, "g");

/** Cross-platform basename: cuts at the last `/` or `\` separator. */
function basename(name: string): string {
  const cut = Math.max(name.lastIndexOf("/"), name.lastIndexOf("\\"));
  return cut >= 0 ? name.slice(cut + 1) : name;
}

function byteLength(text: string): number {
  return new TextEncoder().encode(text).length;
}

/** Trims to `MAX_NAME_BYTES` bytes preserving the extension if it fits. */
function clampBytes(name: string): string {
  if (byteLength(name) <= MAX_NAME_BYTES) {
    return name;
  }
  const dot = name.lastIndexOf(".");
  const ext = dot > 0 ? name.slice(dot) : "";
  const stemBudget = MAX_NAME_BYTES - byteLength(ext);
  let stem = dot > 0 ? name.slice(0, dot) : name;
  while (stem.length > 0 && byteLength(stem) > Math.max(stemBudget, 1)) {
    stem = stem.slice(0, -1);
  }
  const candidate = `${stem}${ext}`;
  return byteLength(candidate) <= MAX_NAME_BYTES ? candidate : stem.slice(0, MAX_NAME_BYTES);
}

/** Sanitizes ONE segment (without separators) already isolated. */
function sanitizeSegment(raw: string): string {
  let name = raw.normalize("NFC");
  name = name.replace(ILLEGAL, "_");
  // Trailing dot/space: illegal (or trimmed) on Windows.
  name = name.replace(/[ .]+$/, "");
  name = name.trim();
  if (name === "" || name === "." || name === "..") {
    return DEFAULT_NAME;
  }
  const dot = name.indexOf(".");
  const stem = dot > 0 ? name.slice(0, dot) : name;
  if (WINDOWS_RESERVED.has(stem.toUpperCase())) {
    name = `_${name}`;
  }
  return clampBytes(name);
}

/** Sanitizes a received file name so it is safe and portable. */
export function sanitizeFilename(raw: string): string {
  return sanitizeSegment(basename(typeof raw === "string" ? raw : ""));
}

/** Maximum folder depth accepted in a received relative path. */
const MAX_PATH_SEGMENTS = 32;

/**
 * Sanitizes a received RELATIVE PATH (folder sending): each segment goes through
 * the same cross-OS rules as a lone name and every traversal attempt is discarded
 * (`..`, absolute, duplicated separators). The result uses `/`
 * as the canonical separator and NEVER leaves the destination directory.
 */
export function sanitizeRelativePath(raw: string): string {
  const segments = (typeof raw === "string" ? raw : "")
    .split(/[/\\]+/)
    .filter((s) => s !== "" && s !== "." && s !== "..");
  if (segments.length === 0) {
    return DEFAULT_NAME;
  }
  return segments.slice(0, MAX_PATH_SEGMENTS).map(sanitizeSegment).join("/");
}

/**
 * Avoids collisions when several files with the same name arrive:
 * `foto.png` → `foto (1).png`, `foto (2).png`… The matching against the set of
 * already used names is handled by the receiver.
 */
export function dedupeFilename(name: string, used: ReadonlySet<string>): string {
  if (!used.has(name)) {
    return name;
  }
  const dot = name.lastIndexOf(".");
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : "";
  for (let i = 1; i < 10_000; i += 1) {
    const candidate = `${stem} (${i})${ext}`;
    if (!used.has(candidate)) {
      return candidate;
    }
  }
  return `${stem} (${Date.now()})${ext}`;
}
